import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

import ConfettiAnimation from './components/ConfettiAnimation';
import MilestoneHeader from './components/MilestoneHeader';
import UnlockedFeatures from './components/UnlockedFeatures';
import ShareOptions from './components/ShareOptions';

const ReferralMilestoneCelebration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [referralLink, setReferralLink] = useState('');

  // Get milestone from URL param (5 or 10), default to 5
  const milestoneParam = parseInt(searchParams?.get('milestone') || '5', 10);
  const milestone = [5, 10]?.includes(milestoneParam) ? milestoneParam : 5;
  const is10 = milestone === 10;

  useEffect(() => {
    if (user) {
      const code = `MBT${user?.id?.slice(0, 8)?.toUpperCase()}`;
      setReferralLink(`${window.location?.origin}/sign-in?ref=${code}`);
    }
  }, [user]);

  const handlePrimaryAction = () => {
    if (is10) {
      navigate('/premium-features-showcase');
    } else {
      navigate('/doctor-summary-export');
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background: is10
          ? 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 50%, #ddd6fe 100%)'
          : 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 50%, #c7d2fe 100%)'
      }}
    >
      {/* Confetti */}
      <ConfettiAnimation intensity={is10 ? 'high' : 'normal'} />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top nav */}
        <div className="flex items-center justify-between px-4 py-4">
          <button
            onClick={handleBackToDashboard}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors bg-white/70 backdrop-blur-sm px-3 py-2 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>

          <div className={`text-xs font-bold px-3 py-1.5 rounded-full ${
            is10 ? 'bg-purple-600 text-white' : 'bg-indigo-600 text-white'
          }`}>
            🎉 {milestone} Referrals Reached!
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-start justify-center px-4 py-6">
          <div className="w-full max-w-lg">
            {/* Header */}
            <MilestoneHeader milestone={milestone} />

            {/* Unlocked Features */}
            <UnlockedFeatures milestone={milestone} />

            {/* Primary CTA */}
            <button
              onClick={handlePrimaryAction}
              className={`w-full py-4 px-6 rounded-2xl font-bold text-white text-lg shadow-lg hover:shadow-xl transition-all mb-4 flex items-center justify-center gap-3 ${
                is10
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700' :'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700'
              }`}
            >
              <Zap className="w-6 h-6" />
              {is10 ? 'Explore Premium Features' : 'Access Doctor Summary Now'}
            </button>

            {/* Share Options */}
            <ShareOptions referralLink={referralLink} />

            {/* Back to Dashboard */}
            <button
              onClick={handleBackToDashboard}
              className="w-full py-3 px-6 rounded-2xl font-medium text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-200 hover:bg-white transition-all text-sm"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralMilestoneCelebration;
